import { generateApiRoute } from '@klp/core/api/helpers';
import { baseApi } from '@klp/core/api';
import {
    brukerApiName,
    byggProsjektkontrollerApiName,
    regionApiName,
    rollerApiName,
    userApiName,
} from '../config';
import type { Bygg } from '@forvalter/types/ForvalterCommonDAL';
import { byggApiName } from '@klp/contextSelector/config';
import type {
    Bruker,
    BrukerByggProsjektKontroller,
    BrukerRegionRequest,
    BrukerTokenAccess,
    ByggProsjektKontroller,
} from '../types';
import { userFetchSuccess } from '../userSlice';

export const brukereApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getBrukere: build.query<Array<Bruker>, void>({
            query: () => ({
                url: generateApiRoute([brukerApiName]),
                method: 'GET',
            }),
            providesTags: ['Brukerroller', 'Brukerregion'],
        }),
        getBruker: build.query<Bruker, { email: string }>({
            query: ({ email }) => ({
                url: generateApiRoute([brukerApiName, userApiName, email]),
                method: 'GET',
            }),
            providesTags: ['Brukerroller', 'Brukerregion'],
        }),
        getRegioner: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([brukerApiName, regionApiName]),
                method: 'GET',
            }),
        }),
        postBrukerRegion: build.mutation<Bruker, BrukerRegionRequest>({
            query(data) {
                return {
                    url: generateApiRoute([
                        brukerApiName,
                        userApiName,
                        regionApiName,
                    ]),
                    method: 'POST',
                    body: data,
                };
            },
            invalidatesTags: ['Brukerregion', 'PortfolioList'],
        }),
        getRoller: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([brukerApiName, rollerApiName]),
                method: 'GET',
            }),
            transformResponse: (response: Array<string>) => response || [],
        }),
        postBrukerRoller: build.mutation<
            Bruker,
            Pick<Bruker, 'brukerId' | 'roller'>
        >({
            query(data) {
                return {
                    url: generateApiRoute([
                        brukerApiName,
                        userApiName,
                        rollerApiName,
                    ]),
                    method: 'POST',
                    body: data,
                };
            },
            invalidatesTags: ['Brukerroller', 'PortfolioList'],
        }),
        getBrukereByggList: build.query<
            Array<Bygg>,
            { ansvarlig?: string; includeAll?: boolean }
        >({
            query: ({ ansvarlig, includeAll }) => ({
                url: generateApiRoute([byggApiName]),
                params: { ansvarlig, includeAll },
            }),
            providesTags: ['ByggList'],
        }),
        getByggProsjektkontrollerList: build.query<
            Array<ByggProsjektKontroller>,
            void
        >({
            query: () => ({
                url: generateApiRoute([
                    brukerApiName,
                    byggProsjektkontrollerApiName,
                ]),
            }),
            providesTags: ['ByggProsjektkontroller'],
        }),
        postByggProsjektkontroller: build.mutation<
            Bruker,
            BrukerByggProsjektKontroller
        >({
            query(data) {
                return {
                    url: generateApiRoute([
                        brukerApiName,
                        userApiName,
                        byggProsjektkontrollerApiName,
                    ]),
                    method: 'POST',
                    body: data,
                };
            },
            invalidatesTags: [
                'Brukerroller',
                'ByggProsjektkontroller',
                'PortfolioList',
            ],
        }),
        getCurrentUser: build.query<Bruker, BrukerTokenAccess>({
            query: () => {
                return {
                    url: generateApiRoute([
                        brukerApiName,
                        userApiName,
                        'bytoken',
                    ]),
                    method: 'GET',
                };
            },
            providesTags: ['CurrentUser'],
            onQueryStarted: async (
                { isAdmin, isEDAdmin },
                { dispatch, queryFulfilled },
            ) => {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        userFetchSuccess({
                            ...data,
                            isAdmin,
                            isEDAdmin,
                        } satisfies Bruker),
                    );
                } catch (error) {
                    console.error('Failed to fetch user data:', error);
                }
            },
        }),
    }),
});

export const {
    useLazyGetBrukereQuery,
    useGetBrukereQuery,
    useGetBrukerQuery,
    useGetRegionerQuery,
    useLazyGetRegionerQuery,
    useLazyGetRollerQuery,
    useGetRollerQuery,
    usePostBrukerRollerMutation,
    usePostBrukerRegionMutation,
    useGetBrukereByggListQuery,
    usePostByggProsjektkontrollerMutation,
    useGetByggProsjektkontrollerListQuery,
    useGetCurrentUserQuery,
} = brukereApi;
